import { Button } from "../Button";
import { useAutoScroll } from "../../hooks";
import { useChatClass, useTheme } from "../../theme";
import { useAnimatedMessages } from "./hooks";
import { Indicator } from "./Indicator";
import { Actions, Content, SystemResponseContainer } from "./styled";
import { SystemMessage } from "./SystemMessage";
import { SystemMessageProps } from "./types";
import { Feedback, FeedbackProps } from "../Feedback";

export type {
  BaseMessageProps,
  CardMessageProps,
  CarouselMessageProps,
  CustomMessage,
  EndMessage,
  ImageMessageProps,
  SystemMessageProps,
  TextMessageProps,
} from "./types";

/** Similar to `CardActionProps` */
export interface ResponseActionProps {
  /** Action label, appears on button */
  name: string;
  /** If URL exists, navigation occurs on click */
  url?: string;
  /** A callback function to be executed when the button is clicked. */
  onClick?: (...args: any) => any;
  type?: "button" | "quick_reply" | "payload" | "form" | "url";
  /** Flexible payload for future/custom functionality */
  payload?: Record<string, any>;
  /** Visual variant; use outline/info as before. */
  variant?: "default" | "action";
  /** Optional preferred size and width for rendering */
  size?: "sm" | "md" | "xl";
  width?: string;
}

export interface SystemResponseProps {
  /** An image URL for an avatar to associate the messages with. */
  avatar: string;
  /** A unix timestamp indicating when the messages were sent. */
  timestamp: number;
  /** Optional preformatted timestamp text. */
  timestampLabel?: string;
  /** Optional human sender name shown beside the timestamp. */
  senderName?: string;
  /** A list of messages that will be rendered as {@link SystemMessage} components. */
  messages: SystemMessageProps[];
  /** A unique identifier for this response */
  id: string;
  /** A list of actions that will be rendered as buttons. */
  actions?: ResponseActionProps[];
  /** Feedback to be displayed below the message. */
  feedback?: FeedbackProps;
  /** `True` if this is the most recent message from the bot. */
  isLast?: boolean;
  /** If `true` show message animations (loading indicator) */
  useAnimation?: boolean;
  /** Callback to be executed when animation is complete (i.e. all messages are showing).
   *
   * Prop `useAnimation` must be `true` for this to work. */
  onEndAnimation?: (messages: SystemMessageProps[]) => any;
  /** Custom styles to apply to the container. */
  messageContainerStyle?: React.CSSProperties;
  /** Optional callback when a link in text messages is clicked */
  onLinkClick?: (href: string) => void;
}

interface SystemResponseComponent extends React.FC<SystemResponseProps> {
  Message: typeof SystemMessage;
  Container: typeof SystemResponseContainer;
  Content: typeof Content;
  Actions: typeof Actions;
  Indicator: typeof Indicator;
}

/** A dynamic component capable of displaying all standard system responses. */
const SystemResponse: SystemResponseComponent = ({
  avatar,
  timestamp,
  timestampLabel,
  senderName,
  messages,
  actions = [],
  feedback,
  isLast = false,
  useAnimation = false,
  onEndAnimation,
  messageContainerStyle,
  id,
  onLinkClick,
}: SystemResponseProps) => {
  const theme = useTheme();
  const chatClass = useChatClass();

  const { showIndicator, visibleMessages, complete } = useAnimatedMessages({
    messages: useAnimation ? messages : [],
    isLast,
    onEndAnimation,
  });

  useAutoScroll([showIndicator, complete, visibleMessages.length]);

  if (!messages.length && !actions.length) return null;

  const filteredMessages = useAnimation ? visibleMessages : messages;

  return (
    <SystemResponseContainer
      className={chatClass("system-response")}
      id={`${chatClass("system-response")}-${id}`}
      aria-label="Assistant message"
    >
      {filteredMessages.map((message, index, arr) => (
        <SystemMessage
          message={message}
          // Show the avatar on the last message
          withImage={!showIndicator && index === arr.length - 1}
          avatar={avatar}
          timestamp={timestamp}
          timestampLabel={timestampLabel}
          senderName={senderName}
          key={index}
          containerStyle={messageContainerStyle}
          onLinkClick={onLinkClick}
        />
      ))}

      {/** Show feedback below the last message */}
      {complete && Boolean(feedback) && (
        <Feedback {...feedback} mode={feedback!.mode} />
      )}

      {/** Always show buttons, even after they are clicked */}
      {complete && Boolean(actions.length) && (
        <Actions
          className={chatClass("system-message-actions")}
          role="group"
          aria-label="Quick reply options"
        >
          {actions.map(({ name, onClick, variant, size, width }, index) => (
            <Button
              variant={variant === "action" ? "info" : "outline"}
              size={size || (variant === "action" ? "xl" : "sm")}
              width={width || (variant === "action" ? "100%" : undefined)}
              onClick={onClick}
              key={index}
              aria-label={`Quick reply: ${name}`}
              style={
                variant === "action"
                  ? undefined
                  : {
                      color: theme.colors.primaryDark,
                      borderColor: theme.colors.primaryDark,
                    }
              }
            >
              {name}
            </Button>
          ))}
        </Actions>
      )}

      {showIndicator && (
        <Indicator
          avatar={avatar}
          containerStyle={{ marginTop: theme.space[5] }}
        />
      )}
    </SystemResponseContainer>
  );
};

SystemResponse.Message = SystemMessage;
SystemResponse.Container = SystemResponseContainer;
SystemResponse.Content = Content;
SystemResponse.Actions = Actions;
SystemResponse.Indicator = Indicator;

export { SystemResponse };
